/**
 * Minimal, privacy-conscious error reporting. Logs structured events to the
 * console in development; in production it forwards to a sink installed with
 * `installErrorSink` (the Sentry adapter in sentry.ts) or as
 * `window.__MT_ERROR_SINK__`, if any. Never put passwords, tokens, emails or
 * answer content in `fields`.
 */
type Fields = Record<string, string | number | boolean | null | undefined>;
export type ErrorEntry = Record<string, unknown> & { event: string };
export type ErrorSink = (entry: ErrorEntry, error?: unknown) => void;

let sink: ErrorSink | undefined;
/** Reports made before a sink is installed (e.g. while Sentry loads). */
const pending: [ErrorEntry, unknown][] = [];
const MAX_PENDING = 10;

function currentSink(): ErrorSink | undefined {
  return sink ?? (typeof window === 'undefined' ? undefined : (window as unknown as { __MT_ERROR_SINK__?: ErrorSink }).__MT_ERROR_SINK__);
}

function deliver(target: ErrorSink, entry: ErrorEntry, error: unknown): void {
  try {
    target(entry, error);
  } catch {
    /* reporting must never break the app */
  }
}

export type ReportLevel = 'error' | 'warning';

/** `error`, when given, lets the sink attach a stack trace. Use 'warning' for events the app recovered from. */
export function reportError(event: string, fields: Fields = {}, error?: unknown, level: ReportLevel = 'error'): void {
  const entry: ErrorEntry = { level, event, ...fields, at: new Date().toISOString() };
  if (import.meta.env.DEV) console.warn('[telemetry]', entry);
  const target = currentSink();
  if (target) deliver(target, entry, error);
  else if (pending.length < MAX_PENDING) pending.push([entry, error]);
}

export function installErrorSink(next: ErrorSink): void {
  sink = next;
  for (const [entry, error] of pending.splice(0)) deliver(next, entry, error);
}

let installed = false;
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => reportError('window_error', { message: String(e.message).slice(0, 200) }));
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason as { message?: string } | undefined;
    reportError('unhandled_rejection', { message: String(reason?.message ?? e.reason).slice(0, 200) });
  });
}
